import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { mockEndpointResponse } from '@/__mocks__/api-endpoints/mock-endpoint-response';
import { mockGetResidents } from '@/__mocks__/api-endpoints/mock-get-residents';
import { mockGetWellbeingResponses } from '@/__mocks__/api-endpoints/mock-get-wellbeing-responses';
import {
  AddressBook,
  Contact,
  ListUnitAddressBooksResponse,
} from '@/types/address-book';
import { AlexaNotification } from '@/types/alexa-notification';
import { ApplicationRole } from '@/types/application-role';
import { ApplicationUser } from '@/types/application-user';

import { Call } from '@/types/call';
import { ConnectivityResponse } from '@/types/connectivity';
import {
  DeleteUnitFfConnectionResponse,
  GetUnitFfConnectionsResponse,
} from '@/types/ff-connection';
import { Group } from '@/types/group';
import { GroupAnalyticsUnit } from '@/types/group-analytics';
import { Organisation } from '@/types/organisation';
import { Policy } from '@/types/policy';
import { Resident } from '@/types/resident';
import { Statistic } from '@/types/statistic';
import { Unit } from '@/types/unit';
import { Response } from '@/types/wellbeing-response';
import { getEnv } from '@/utils/get-env';

import {
  AddResidentToUnitQueryArg,
  addResidentToUnitQueryBody,
  handleResultAddResidentToUnit,
} from './endpoints/add-resident-to-unit';
import {
  CreateCredentialsQueryArg,
  createCredentialsQueryBody,
} from './endpoints/create-credentials';
import {
  CreateOrganisationQueryArg,
  handleResultCreateOrganisation,
} from './endpoints/create-organisation';
import {
  CreateUnitQueryArg,
  createUnitQueryBody,
  handleResultCreateUnit,
} from './endpoints/create-unit';
import {
  CreateUserQueryArg,
  createUserQueryBody,
  handleResultCreateUser,
} from './endpoints/create-user';
import {
  DeleteOrganisationQueryArg,
  handleResultDeleteOrganisation,
} from './endpoints/delete-organisation';
import {
  DeleteUserQueryArg,
  handleResultDeleteUser,
} from './endpoints/delete-user';

import {
  GetConnectivityQueryArg,
  getConnectivityQueryParams,
} from './endpoints/get-connectivity';
import { GetGroupsQueryArg } from './endpoints/get-groups';
import {
  GetPoliciesQueryArg,
  getPoliciesQueryParams,
} from './endpoints/get-policies';
import { GetResidentsQueryArg } from './endpoints/get-residents';
import { GetResidentInUnitQueryArg } from './endpoints/get-residents-in-unit';
import { GetStatisticsQueryArg } from './endpoints/get-statistics';
import { GetUnitsQueryArg, getUnitsQueryParams } from './endpoints/get-units';
import { GetResponsesQueryArg } from './endpoints/get-wellbeing-responses';
import {
  SendAnnouncementQueryArg,
  SendAnnouncementQueryArgBody,
} from './endpoints/post-notifications';

import {
  handleResultUpdateOrganisation,
  UpdateOrganisationQueryArg,
} from './endpoints/update-organisation';
import {
  handleResultUpdateAccessibilitySettings,
  UpdateAccessibilitySettingsQueryArg,
  updateAccessibilitySettingsQueryBody,
} from './endpoints/update-policy-accessibility-settings';
import {
  handleResultUpdateAlexaSkills,
  UpdateAlexaSkillsQueryArg,
  updateAlexaSkillsQueryBody,
} from './endpoints/update-policy-alexa-skills';
import {
  handleResultUpdateWellbeingCheckSettings,
  UpdateWellbeingCheckSettingsQueryArg,
  updateWellbeingCheckSettingsQueryBody,
} from './endpoints/update-policy-wellbeing-check-settings';
import {
  handleResultUpdateUser,
  // handleResultUpdateUser,
  UpdateUserQueryArg,
  updateUserQueryBody,
} from './endpoints/update-user';

import type { RootState } from '../store';
import { QueryReturnValue } from '../types/query-return-value';

const mocking = false;

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: getEnv().VITE_BACKEND_API_BASE_URL,
    prepareHeaders: async (headers) => {
      const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
      if (idToken) {
        headers.set('Authorization', `Bearer ${idToken}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    'UnimportedDevices',
    'Devices',
    'AddressBook',
    'User',
    'FfConnections',
  ],
  endpoints: (builder) => ({
    checkApi: builder.query<unknown, { path: string }>({
      query: ({ path }) => path,
    }),
    getUser: builder.query<ApplicationUser, void>({
      query: () => '/me',
    }),
    getUsers: builder.query<ApplicationUser[], void>({
      query: () => '/user',
      providesTags: ['User'],
    }),
    createUser: builder.mutation<ApplicationUser, CreateUserQueryArg>({
      query: (queryArg) => ({
        url: '/user',
        method: 'POST',
        body: createUserQueryBody(queryArg),
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultCreateUser(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    updateUser: builder.mutation<ApplicationUser, UpdateUserQueryArg>({
      query: (queryArg) => ({
        url: '/user',
        method: 'PUT',
        body: updateUserQueryBody(queryArg),
        providesTags: ['user'],
      }),
      onQueryStarted: async (queryArg, api) => {
        const toastId = toast.loading('Updating user...');
        try {
          await handleResultUpdateUser(queryArg, api);
          toast.success('User updated successfully', {
            id: toastId,
          });
        } catch {
          // TODO: throw toast error
          toast.error('User failed to update', {
            id: toastId,
          });
        }
      },
    }),
    deleteUser: builder.mutation<void, DeleteUserQueryArg>({
      query: (queryArg) => ({
        url: '/user',
        method: 'DELETE',
        params: queryArg,
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultDeleteUser(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    getStatistics: builder.query<Statistic[], GetStatisticsQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisationId,
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/statistic',
          params: { organisationId: queryArg.organisationId },
        });

        return result as QueryReturnValue<Statistic[]>;
      },
    }),
    getRoles: builder.query<ApplicationRole[], void>({
      query: () => '/role',
    }),
    getOrganisations: builder.query<Organisation[], void>({
      query: () => '/organisation',
    }),
    createOrganisation: builder.mutation<
      Organisation,
      CreateOrganisationQueryArg
    >({
      query: (queryArg) => ({
        url: '/organisation',
        method: 'POST',
        body: queryArg,
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultCreateOrganisation(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    updateOrganisation: builder.mutation<void, UpdateOrganisationQueryArg>({
      query: (queryArg) => ({
        url: '/organisation',
        method: 'PUT',
        body: queryArg,
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultUpdateOrganisation(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    deleteOrganisation: builder.mutation<void, DeleteOrganisationQueryArg>({
      query: (queryArg) => ({
        url: '/organisation',
        method: 'DELETE',
        params: queryArg,
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultDeleteOrganisation(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    getAllGroups: builder.query<Group[], void>({
      query: () => '/group',
    }),
    getGroups: builder.query<Group[], GetGroupsQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisationId ?? 'all',
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/group',
          params:
            queryArg.organisationId != null
              ? { organisationId: queryArg.organisationId }
              : {},
        });

        return result as QueryReturnValue<Group[]>;
      },
    }),
    createGroup: builder.mutation<
      Group,
      { organisationId: number; groupName: string }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/group',
          method: 'POST',
          body: {
            groupName: queryArg.groupName,
            organisationId: queryArg.organisationId,
          },
        });
        return result as QueryReturnValue<Group>;
      },
      // Update the getGroups cache with the returned result
      onQueryStarted: async (
        queryArg,
        { dispatch, queryFulfilled, getState },
      ) => {
        const toastId = toast.loading('Creating group...');

        try {
          // Wait for the mutation to complete
          const { data: newGroup } = await queryFulfilled;

          // Ensure the group has full organization data
          // If the API doesn't return it, we'll get it from the organisations cache
          let enrichedGroup = newGroup;
          if (!newGroup.organisation || !newGroup.organisation.name) {
            const state = getState() as RootState;
            const organisationsCache =
              api.endpoints.getOrganisations.select(undefined)(state);
            const organisations = organisationsCache?.data;

            if (organisations) {
              const organisation = organisations.find(
                (org: Organisation) => org.id === queryArg.organisationId,
              );

              if (organisation) {
                enrichedGroup = {
                  ...newGroup,
                  organisation: {
                    id: organisation.id,
                    name: organisation.name,
                    active: organisation.active,
                  },
                };
              }
            }
          }

          // Update the getGroups cache with the new group
          dispatch(
            api.util.updateQueryData(
              'getGroups',
              { organisationId: queryArg.organisationId },
              (draft) => {
                draft.push(enrichedGroup);
              },
            ),
          );

          // Update the getAllGroups cache for platform admins
          dispatch(
            api.util.updateQueryData('getAllGroups', undefined, (draft) => {
              draft.push(enrichedGroup);
            }),
          );

          toast.success('Group created successfully', {
            id: toastId,
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          toast.error('Failed to create group', {
            id: toastId,
          });
        }
      },
    }),
    updateGroup: builder.mutation<Group, { id: number; groupName: string }>({
      query: (queryArg) => ({
        url: '/group',
        method: 'PUT',
        body: queryArg,
      }),
      onQueryStarted: async (
        queryArg,
        { dispatch, queryFulfilled, getState },
      ) => {
        const toastId = toast.loading('Updating group...');

        /**
         * Get the organisationId from the getGroups cache
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = getState() as { api: { queries: any } };
        const organisationId = Object.keys(state.api.queries)
          .filter((key) => key.startsWith('getGroups'))
          .map((key) => state.api.queries[key].originalArgs.organisationId)[0];

        /**
         * Dispatch an optimistic update to the getGroups cache. We don't need
         * to do anything substantial here, as the groupName is the only field
         * that is updated.
         */
        const patchGetGroups = organisationId
          ? dispatch(
              api.util.updateQueryData(
                'getGroups',
                { organisationId },
                (draft) => {
                  const index = draft.findIndex(
                    (group) => group.id === queryArg.id,
                  );
                  if (index !== -1) {
                    draft[index].groupName = queryArg.groupName;
                  }
                },
              ),
            )
          : null;

        /**
         * Dispatch an optimistic update to the getAllGroups cache for platform admins
         */
        const patchGetAllGroups = dispatch(
          api.util.updateQueryData('getAllGroups', undefined, (draft) => {
            const index = draft.findIndex((group) => group.id === queryArg.id);
            if (index !== -1) {
              draft[index].groupName = queryArg.groupName;
            }
          }),
        );

        try {
          await queryFulfilled;
          toast.success('Group updated successfully', {
            id: toastId,
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          patchGetGroups?.undo();
          patchGetAllGroups.undo();
          toast.error('Failed to update group', {
            id: toastId,
          });
        }
      },
    }),
    deleteGroup: builder.mutation<void, { id: number }>({
      query: (queryArg) => ({
        url: '/group',
        method: 'DELETE',
        params: queryArg,
      }),
      // Update the getGroups cache by removing the deleted group
      onQueryStarted: async (
        queryArg,
        { dispatch, queryFulfilled, getState },
      ) => {
        const toastId = toast.loading('Deleting group...');

        /**
         * Get the organisationId from the getGroups cache
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = getState() as { api: { queries: any } };
        const organisationId = Object.keys(state.api.queries)
          .filter((key) => key.startsWith('getGroups'))
          .map((key) => state.api.queries[key].originalArgs.organisationId)[0];

        // Optimistically remove the group from the getGroups cache
        const patchGetGroups = organisationId
          ? dispatch(
              api.util.updateQueryData(
                'getGroups',
                { organisationId },
                (draft) => {
                  const index = draft.findIndex(
                    (group) => group.id === queryArg.id,
                  );
                  if (index !== -1) {
                    draft.splice(index, 1);
                  }
                },
              ),
            )
          : null;

        // Optimistically remove the group from the getAllGroups cache
        const patchGetAllGroups = dispatch(
          api.util.updateQueryData('getAllGroups', undefined, (draft) => {
            const index = draft.findIndex((group) => group.id === queryArg.id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );

        try {
          await queryFulfilled;
          toast.success('Group deleted successfully', {
            id: toastId,
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          patchGetGroups?.undo();
          patchGetAllGroups.undo();
          toast.error('Failed to delete group', {
            id: toastId,
          });
        }
      },
    }),
    getUnits: builder.query<Unit[], GetUnitsQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisation?.id,
      query: (queryArg) => ({
        url: '/unit',
        params: getUnitsQueryParams(queryArg),
      }),
      transformResponse: (
        baseQueryReturnValue: Omit<Unit, 'organisation'>[],
        _meta,
        { organisation },
      ) =>
        baseQueryReturnValue.map((unit) => ({
          ...unit,
          organisation: { ...organisation },
        })),
    }),
    getGroupAnalytics: builder.query<
      GroupAnalyticsUnit[],
      { groupId: number; startDate?: string; endDate?: string }
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs.groupId}:${queryArgs.startDate ?? ''}:${queryArgs.endDate ?? ''}`,
      query: ({ groupId, startDate, endDate }) => ({
        url: `/group/${groupId}/analytics`,
        params: {
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      }),
    }),
    getConnectivity: builder.query<
      ConnectivityResponse,
      GetConnectivityQueryArg
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs.unitId}:${queryArgs.page ?? 1}:${queryArgs.limit ?? 50}`,
      query: (queryArg) => ({
        url: '/connectivity',
        params: getConnectivityQueryParams(queryArg),
      }),
      transformResponse: (base: {
        items: unknown[];
        meta: { page: number; limit: number; hasMore: boolean };
      }) => ({
        items: base.items as unknown as ConnectivityResponse['items'],
        meta: base.meta,
      }),
    }),
    getCalls: builder.query<Call[], { unitId: number }>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.unitId,
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/call',
          params: { unitId: queryArg.unitId },
        });

        return result as QueryReturnValue<Call[]>;
      },
    }),
    createUnit: builder.mutation<Unit, CreateUnitQueryArg>({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const mocking = false;
        if (mocking) {
          await mockEndpointResponse(undefined, 3000)({ reject: false });
          const mockUnit: Unit = {
            id: 100,
            unitName: queryArg.unitName,
            devices: [],
            organisation: queryArg.organisation,
            uuid: null,
          };

          return { data: mockUnit };
        }
        const result = await baseQuery({
          url: '/unit',
          method: 'POST',
          body: createUnitQueryBody(queryArg),
        });
        return result as QueryReturnValue<Unit>;
      },
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultCreateUnit(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    resetUnit: builder.mutation<
      boolean,
      { organisationId: number; unitId: number; actionType: string }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/unit',
          method: 'PATCH',
          body: queryArg,
        });
        return result as QueryReturnValue<boolean>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { queryFulfilled }) => {
        const toastId = toast.loading('Resetting unit...');
        try {
          await queryFulfilled;
          toast.success('Successfully reset unit!', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to reset unit', {
            id: toastId,
          });
        }
      },
    }),
    assignAddressBook: builder.mutation<
      boolean,
      {
        organisationId: number;
        unitId: number;
        actionType: 'assignAddressBook';
        addressBookId: string;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<boolean>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Assigning address book to unit...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully assigned address book', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to assign address book', {
            id: toastId,
          });
        }
      },
    }),
    unassignAddressBook: builder.mutation<
      boolean,
      {
        organisationId: number;
        unitId: number;
        actionType: 'unassignAddressBook';
        addressBookId: string;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<boolean>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Removing address book from unit...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully removed address book', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to remove address book', {
            id: toastId,
          });
        }
      },
    }),
    getUnitFfConnections: builder.query<
      GetUnitFfConnectionsResponse,
      { unitId: number }
    >({
      query: ({ unitId }) => ({
        url: `/unit/${unitId}/ff-connections`,
        method: 'GET',
      }),
      providesTags: ['FfConnections'],
    }),
    deleteUnitFfConnection: builder.mutation<
      DeleteUnitFfConnectionResponse,
      { unitId: number; connectionId: number }
    >({
      query: ({ unitId, connectionId }) => ({
        url: `/unit/${unitId}/ff-connections/${connectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FfConnections'],
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        const toastId = toast.loading('Breaking connection...');
        try {
          await queryFulfilled;
          toast.success('Connection broken', { id: toastId });
        } catch {
          toast.error('Failed to break connection. Please try again.', {
            id: toastId,
          });
        }
      },
    }),
    listUnitAddressBooks: builder.query<
      ListUnitAddressBooksResponse,
      {
        organisationId: number;
        unitId: number;
        actionType: 'listUnitAddressBooks';
      }
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.unitId,
      providesTags: ['AddressBook'],
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'GET',
          params: queryArg,
        });
        return result as QueryReturnValue<ListUnitAddressBooksResponse>;
      },
    }),
    listAddressBooks: builder.query<
      AddressBook[],
      {
        organisationId: number;
        unitId: number;
        actionType: 'listAddressBooks';
      }
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.unitId,
      providesTags: ['AddressBook'],
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'GET',
          params: queryArg,
        });
        return result as QueryReturnValue<AddressBook[]>;
      },
    }),
    createAddressBookContact: builder.mutation<
      string,
      {
        addressBookId: string;
        contact: {
          name: string;
          phoneNumber: string;
        };
        actionType: 'createAddressBookContact';
        organisationId: number;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<string>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Creating contact in address book...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully created address book contact', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to create address book contact', {
            id: toastId,
          });
        }
      },
    }),
    alignAddressBooks: builder.mutation<
      boolean,
      {
        actionType: 'alignAddressBooks';
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<boolean>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Updating contact in address book...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully updated address book contact', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to update address book contact', {
            id: toastId,
          });
        }
      },
    }),
    updateAddressBookContact: builder.mutation<
      string,
      {
        addressBookId: string;
        contact: Contact;
        contactId: string;
        actionType: 'updateAddressBookContact';
        organisationId: number;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<string>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Updating contact in address book...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully updated address book contact', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to update address book contact', {
            id: toastId,
          });
        }
      },
    }),
    getAddressBookWithContacts: builder.query<
      AddressBook,
      {
        organisationId: number;
        actionType: 'getAddressBookWithContacts';
        addressBookId: string;
      }
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs,
      providesTags: ['AddressBook'],
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'GET',
          params: queryArg,
        });
        return result as QueryReturnValue<AddressBook>;
      },
    }),
    deleteAddressBookContact: builder.mutation<
      string,
      {
        organisationId: number;
        addressBookId: string;
        contactId: string;
        actionType: 'deleteAddressBookContact';
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<string>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Deleting contact in address book...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully deleted address book contact', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to delete address book contact', {
            id: toastId,
          });
        }
      },
    }),
    createAssignAddressBook: builder.mutation<
      string,
      {
        actionType: 'createAssignAddressBook';
        organisationId: number;
        unitId: number;
        addressBookName: string;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/address-book',
          method: 'POST',
          body: queryArg,
        });
        return result as QueryReturnValue<string>;
      },
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Creating address book...');
        try {
          await queryFulfilled;
          dispatch(api.util.invalidateTags(['AddressBook']));
          toast.success('Successfully created address book contact', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to create address book contact', {
            id: toastId,
          });
        }
      },
    }),
    /**
     * The organisation although logically not required for the _server_ update
     * is required here for the optimistic update to the getUnits cache.
     */
    updateUnit: builder.mutation<
      Unit,
      {
        id: number;
        unitName?: string;
        groupId?: number;
        email?: string;
        organisation: { name: string; id: number };
        uuid?: string | null;
      }
    >({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/unit',
          method: 'PUT',
          body: queryArg,
        });

        return result as QueryReturnValue<Unit>;
      },
      onQueryStarted: async (queryArg, { dispatch, queryFulfilled }) => {
        /**
         * The getUnits query is the only place that the unit is stored in the cache.
         * These are consumed by the useCurrentUnit hook, etc. They have the organisation
         * id passed to them, so that platform admins can switch between organisations.
         *
         * Optimistic update:
         */
        const patch = dispatch(
          api.util.updateQueryData(
            'getUnits',
            { organisation: queryArg.organisation },
            (unitCache) => {
              const index = unitCache.findIndex(
                (unit) => unit.id === queryArg.id,
              );
              if (index !== -1) {
                unitCache[index].email = queryArg.email;
                unitCache[index].group = queryArg.groupId
                  ? {
                      id: queryArg.groupId,
                      groupName: '',
                    }
                  : undefined;
              }
            },
          ),
        );

        /**
         * Mutate local getUnits cache on success
         * Rollback optimistic update on failure
         */
        try {
          const { data } = await queryFulfilled;
          dispatch(
            api.util.updateQueryData(
              'getUnits',
              { organisation: queryArg.organisation },
              (draft) => {
                const index = draft.findIndex((unit) => unit.id === data.id);
                if (index !== -1) {
                  // Replace optimistic data with confirmed data from the server
                  draft[index] = data;
                }
              },
            ),
          );
        } catch (error) {
          patch.undo();
          throw error;
        }
      },
    }),
    deleteUnit: builder.mutation<
      void,
      { id: number; organisation: { id: number; name: string } }
    >({
      query: (queryArg) => ({
        url: '/unit',
        method: 'DELETE',
        params: { unitId: queryArg.id },
      }),
      onQueryStarted: async (
        { id, organisation },
        { dispatch, queryFulfilled },
      ) => {
        const toastId = toast.loading('Deleting unit...');

        /**
         * Optimistically remove the unit from the cache
         */
        const patchResult = dispatch(
          api.util.updateQueryData('getUnits', { organisation }, (draft) => {
            const index = draft.findIndex((unit) => unit.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );

        try {
          await queryFulfilled;
          toast.success('Unit deleted successfully', {
            id: toastId,
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          patchResult.undo();

          toast.error('Failed to delete unit', {
            id: toastId,
          });
        }
      },
    }),
    getResponses: builder.query<Response[], GetResponsesQueryArg>({
      serializeQueryArgs: ({ queryArgs }) =>
        queryArgs.type
          ? `${queryArgs.unitId}-${queryArgs.type}`
          : queryArgs.unitId,
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        if (mocking) {
          return await mockGetWellbeingResponses(2000)();
        }
        const result = await baseQuery({
          url: '/wellbeing',
          params: queryArg,
        });
        return result as QueryReturnValue<Response[]>;
      },
    }),
    sendAnnouncementToUnit: builder.mutation<
      Response | string,
      SendAnnouncementQueryArg
    >({
      query: (body) => ({
        url: '/announcements',
        method: 'POST',
        body: SendAnnouncementQueryArgBody(body),
      }),
      // eslint-disable-next-line no-empty-pattern
      onQueryStarted: async (queryArg, { dispatch, queryFulfilled }) => {
        const toastId = toast.loading('Sending announcement...');

        try {
          const { data: newAnnouncement } = await queryFulfilled;

          if (
            queryArg.notificationType == 'responseNotification' &&
            typeof newAnnouncement != 'string'
          ) {
            dispatch(
              api.util.updateQueryData(
                'getResponses',
                { unitId: queryArg.unitIds[0] },
                (draft) => {
                  draft.push(newAnnouncement);
                },
              ),
            );
          }

          await queryFulfilled;
          toast.success('Successfully sent announcement', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to send announcement', {
            id: toastId,
          });
        }
      },
    }),
    getNotifications: builder.query<AlexaNotification, void>({
      query: () => ({
        url: '/announcements',
        method: 'GET',
      }),
    }),
    getPolicies: builder.query<Policy, GetPoliciesQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisation.id,
      query: (queryArg) => ({
        url: '/policies',
        params: getPoliciesQueryParams(queryArg),
      }),
      transformResponse: (
        baseQueryReturnValue: Omit<Policy, 'organisation'>,
        _meta,
        { organisation },
      ) => ({
        ...baseQueryReturnValue,
        organisation: { ...organisation },
      }),
    }),
    updatePoliciesAlexaSkills: builder.mutation<
      void,
      UpdateAlexaSkillsQueryArg
    >({
      query: (queryArg) => ({
        url: '/policies',
        method: 'PUT',
        body: updateAlexaSkillsQueryBody(queryArg),
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultUpdateAlexaSkills(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    updatePoliciesWellbeingCheckSettings: builder.mutation<
      void,
      UpdateWellbeingCheckSettingsQueryArg
    >({
      query: (queryArg) => ({
        url: '/policies',
        method: 'PUT',
        body: updateWellbeingCheckSettingsQueryBody(queryArg),
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultUpdateWellbeingCheckSettings(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    updatePoliciesAccessibilitySettings: builder.mutation<
      void,
      UpdateAccessibilitySettingsQueryArg
    >({
      query: (queryArg) => ({
        url: '/policies',
        method: 'PUT',
        body: updateAccessibilitySettingsQueryBody(queryArg),
      }),
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultUpdateAccessibilitySettings(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    getResidents: builder.query<Resident[], GetResidentsQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query,
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        if (mocking) {
          return await mockGetResidents(2000)();
        }
        const result = await baseQuery({
          url: '/resident',
          params: queryArg,
        });
        return result as QueryReturnValue<Resident[]>;
      },
    }),
    getResidentsInUnit: builder.query<Resident[], GetResidentInUnitQueryArg>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.unitId,
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const mocking = false;
        if (mocking) {
          return await mockGetResidents(3000)({
            reject: false,
          });
        }
        const result = await baseQuery({
          url: '/resident',
          params: queryArg,
        });
        return result as QueryReturnValue<Resident[]>;
      },
    }),
    addResidentToUnit: builder.mutation<void, AddResidentToUnitQueryArg>({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const mocking = false;
        if (mocking) {
          return mockEndpointResponse(undefined, 3000)({ reject: false });
        }
        const result = await baseQuery({
          url: '/resident',
          method: 'POST',
          body: addResidentToUnitQueryBody(queryArg),
        });
        return result as QueryReturnValue<void>;
      },
      onQueryStarted: async (queryArg, api) => {
        try {
          await handleResultAddResidentToUnit(queryArg, api);
        } catch {
          // TODO: throw toast error
        }
      },
    }),
    createCredentials: builder.mutation<void, CreateCredentialsQueryArg>({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/credentials',
          method: 'POST',
          body: createCredentialsQueryBody(queryArg),
        });
        return result as QueryReturnValue<void>;
      },
      onQueryStarted: async (_queryArg, { queryFulfilled }) => {
        const toastId = toast.loading('Creating API Credentials...');
        try {
          await queryFulfilled;
          toast.success('Successfully created credentials', {
            id: toastId,
          });
        } catch {
          toast.error('Failed to create credentials', {
            id: toastId,
          });
        }
      },
    }),
  }),
});
